import axios from "axios";
import { TRYON_SERVER_URL, ADMIN_SHOP_URL } from "../config";
import { resizeUploadedFile, dataURLToBlob } from "../CommonUtils";

const fetchImages = async (url, accessToken=null) => {
    try{
        console.log("Fetching images...")
        let headers = {
            'Content-Type': 'application/json',
        }

        if(accessToken !== null){
            headers.Authorization = `Bearer ${accessToken}`
        }

        let res = await axios.get(url, {
            headers: headers
        })
        
        if(res.status === 200){
            console.log("Images fetched:", res.data)
            return {data: res.data.results, next: res.data.next, previous: res.data.previous, count: res.data.count, status: "success", statusCode: res.status, message: "Images fetched successfully"}
        }else{
            console.log("Failed to fetch images")
            return {data: null, next: null, previous: null, count: 0, status: "error", statusCode: res.status, message: "Failed to fetch images"}
        }
    }catch(error){
        console.error("Error fetching images:", error)
        return {data: null, next: null, previous: null, count: 0, status: "error", statusCode: 400, message: "Error fetching images: " + error}
    }
}

/**
 * Fetch all images from the server
 * @param {string} baseUrl - The base URL of the server
 * @param {string} accessToken - The access token for the user
 * @param {boolean} fetchUserImages - Whether to fetch user images
 * @param {boolean} fetchPublicImages - Whether to fetch public images
 * @param {Object} filters - The filters to apply to the images
 * @returns {Promise<Object>} - A promise that resolves to the response from the server
 */
const fetchAllImages = async (baseUrl, accessToken=null, fetchUserImages=false, fetchPublicImages=true, params={}) => {
    try{
        console.log("Fetching all images...")
        let allImages = [];
        let errorFetchingUserImages = false;
        let errorFetchingPublicImages = false;
        let message = "";

        if(params.type !== "all" && params.type !== undefined){
            baseUrl += `type=${params.type}&`;
        }

        if(params.gender !== "all" && params.gender !== undefined){
            baseUrl += `gender=${params.gender}&`;
        }

        if(params.limit !== undefined){
            baseUrl += `limit=${params.limit}&`;
        }

        if(params.offset !== undefined){
            baseUrl += `offset=${params.offset}&`;
        }

        let nextUrl = null;
        let previousUrl = null;
        let count = 0;

        if(fetchUserImages){
            console.log("Fetching user images...")
            let privateUrl = `${baseUrl}&access=private`
            console.log("Private URL:", privateUrl)
            let response = await fetchImages(privateUrl, accessToken)
            
            if(response.status === "success"){
                console.log("User images fetched:", response.data)
                allImages = [...allImages, ...response.data]
                nextUrl = response.next
                previousUrl = response.previous
                count = response.count
            }else{
                errorFetchingUserImages = true;
                message = response.message;
            }
        }

        if (fetchPublicImages){
            console.log("Fetching public images...")
            let publicUrl = `${baseUrl}&access=public&shop=${ADMIN_SHOP_URL}`
            console.log("Public URL:", publicUrl)
            let response = await fetchImages(publicUrl, null)
            
            if(response.status === "success"){
                console.log("Public images fetched:", response.data)
                allImages = [...allImages, ...response.data]
                nextUrl = response.next
                previousUrl = response.previous
                count = response.count
            }else{
                errorFetchingPublicImages = true;
                message = response.message;
            }
        }

        if(!errorFetchingUserImages && !errorFetchingPublicImages && fetchUserImages && fetchPublicImages){
            console.log("All images:", allImages)
            return {data: allImages, status: "success", next: nextUrl, previous: previousUrl, count: count, statusCode: 200, message: "Images fetched successfully"}
        }

        if(!errorFetchingUserImages && fetchUserImages){
            console.log("User images fetched successfully")
            return {data: allImages, status: "success", next: nextUrl, previous: previousUrl, count: count, statusCode: 200, message: "Images fetched successfully"}
        }

        if(!errorFetchingPublicImages && fetchPublicImages){
            console.log("Public images fetched successfully")
            return {data: allImages, status: "success", next: nextUrl, previous: previousUrl, count: count, statusCode: 200, message: "Images fetched successfully"}
        }

        if(errorFetchingUserImages && errorFetchingPublicImages){
            console.log("Error fetching images:", message)
            return {data: null, status: "error", next: nextUrl, previous: previousUrl, count: count, statusCode: 400, message: message}
        }
    }catch(error){
        console.error("Error fetching images:", error)
        return {data: null, status: "error", statusCode: 400, message: "Error fetching images: " + error}
    }
}

/**
 * Delete an image
 * @param {string} imageId - The ID of the image to delete
 * @param {string} accessToken - The access token for the user
 * @returns {Promise<Object>} - A promise that resolves to the response from the server
 */
const deleteExperimentImage = async (imageId, accessToken) => {
    try {
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
        };
        let baseUrl = `${TRYON_SERVER_URL}/api/v1/experiment_image/`;
    
        const deleteImageUrl = `${baseUrl}${imageId}/`;
        console.log("delete image URL", deleteImageUrl);
    
        const response = await axios.delete(deleteImageUrl, {
            headers: headers
        });

        if (response.status === 204) {
            return {status: "success", statusCode: 204, message: "Image deleted successfully"}  
        } else {
            return {status: "error", statusCode: response.status, message: "Failed to delete image"}
        }
    } catch (error) {
        console.error("Error deleting image:", error.message);
        return {status: "error", statusCode: 400, message: "Error deleting image: " + error}
    }
}

const uploadExperimentImage = async (file, accessToken=null, params={}) => {
    try {
        console.log("Uploading experiment image...")
        let baseUrl = `${TRYON_SERVER_URL}/api/v1/experiment_image/`;

        const resizedImageUrl = await resizeUploadedFile(file);
        const resizedFile = dataURLToBlob(resizedImageUrl);
        console.log("after resize", file?.name, resizedFile);

        const formData = new FormData();
        if(params.type !== undefined){
            formData.append('type', params.type);
        }
        if(params.access !== undefined){
            formData.append('access', params.access);
        }

        if(params.preprocess !== undefined){
            formData.append('preprocess', params.preprocess);
        }

        formData.append('image', resizedFile, file?.name);

        // check if the access token is null, if it is null, return an error
        if(accessToken === null){
            return {status: "error", statusCode: 401, message: "User not logged in. Please Log in and try Again."}
        }

        let headers = {};
        if (accessToken) {
            headers = {
                'Authorization': `Bearer ${accessToken}`
            };
        }
        const response = await fetch(baseUrl, {
            method: 'POST',
            headers: headers,
            body: formData,
        });

        if (!response.ok) {
            if (response.status === 401) {
                return {status: "error", statusCode: 401, message: "User not logged in. Please Log in and try Again."}
            } else {
                return {status: "error", statusCode: response.status, message: "Failed to upload image"}
            }
        }

        const data = await response.json();
        console.log("upload image response:", data);

        return {status: "success", statusCode: 201, data: data, message: "Image uploaded successfully"}
    } catch (error) {
        console.error("Error uploading image:", error.message);
        return {status: "error", statusCode: 400, message: "Error uploading image: " + error}
    }
}

/**
 * Fetch an experiment image
 * @param {string} experimentId - The ID of the experiment to fetch
 * @param {string} accessToken - The access token for the user
 * @returns {Promise<Object>} - A promise that resolves to the response from the server
 */
const fetchExperimentImage = async (experimentId, accessToken) => {
    try {
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
        };
        let baseUrl = `${TRYON_SERVER_URL}/api/v1/experiment_image/`;
        let url = `${baseUrl}${experimentId}/`;
        let response = await axios.get(url, {headers: headers});
        if(response.status === 200){
            return {status: "success", statusCode: 200, data: response.data, message: "Experiment image fetched successfully"}
        }else{
            return {status: "error", statusCode: response.status, message: "Failed to fetch experiment image"}
        }
    } catch (error) {
        console.error("Error fetching experiment image:", error.message);
        return {status: "error", statusCode: 400, message: "Error fetching experiment image: " + error}
    }
}
export {fetchAllImages, deleteExperimentImage, uploadExperimentImage, fetchExperimentImage};